'use client'

import { useState, useEffect, use } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { login, signup, resetPassword } from './actions'
import { 
  EyeIcon, 
  EyeSlashIcon, 
  SparklesIcon, 
  UserIcon, 
  LockClosedIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  HomeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

export default function LoginPage({ searchParams }: { searchParams: Promise<{ message?: string }> }) {
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  // Unwrap searchParams avec React.use()
  const params = use(searchParams)

  useEffect(() => {
    setMounted(true)
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleSubmit = async (formData: FormData) => {
    setIsLoading(true)
    try {
      if (showForgotPassword) {
        await resetPassword(formData)
      } else if (isLogin) {
        await login(formData)
      } else {
        await signup(formData)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Animations variants
  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  }

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  }

  const slideInFromLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  }

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  }

  if (!mounted) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50/30 via-white to-purple-50/30 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Bouton retour à l'accueil avec animations */}
      <motion.div 
        variants={slideInFromLeft}
        initial="hidden"
        animate="show"
        className="absolute top-4 left-4 sm:top-8 sm:left-8 z-20"
      >
        <Link href="/">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group flex items-center space-x-2 sm:space-x-3 px-4 py-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-violet-200/50 hover:bg-white/90 hover:shadow-xl transition-all duration-300 hover:border-violet-300"
          >
            <div className="flex items-center space-x-2">
              <motion.div
                animate={{ x: [-2, 0, -2] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ArrowLeftIcon className="w-4 h-4 text-violet-600" />
              </motion.div>
              <HomeIcon className="w-5 h-5 text-violet-600" />
            </div>
            <span className="text-sm font-semibold text-gray-700 group-hover:text-violet-700 transition-colors">
              Retour à l'accueil
            </span>
          </motion.button>
        </Link>
      </motion.div>
      
      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        {/* Header avec logo animé - style landing page */}
        <motion.div 
          variants={fadeInUp}
          className="text-center mt-16 sm:mt-0"
        >
          <motion.div 
            whileHover={{ rotate: 360, scale: 1.1 }}
            transition={{ duration: 0.6 }}
            className="mx-auto w-16 h-16 bg-gradient-to-r from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-xl mb-8 relative overflow-hidden group"
          >
            {/* Effet de brillance */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            <SparklesIcon className="w-8 h-8 text-white relative" />
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 mb-4 leading-relaxed"
          >
            <motion.span
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="inline-block bg-gradient-to-r from-gray-900 via-violet-600 to-gray-900 bg-[length:200%_100%] bg-clip-text text-transparent"
            >
              {showForgotPassword ? 'Récupération' : isLogin ? 'Bon retour !' : 'Bienvenue !'}
            </motion.span>
          </motion.h2>

          <motion.p 
            variants={fadeInUp}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {showForgotPassword 
              ? 'Entrez votre email pour réinitialiser'
              : isLogin 
                ? (
                  <>
                    Connectez-vous à{" "}
                    <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                      ConnectRH
                    </span>
                  </>
                )
                : (
                  <>
                    Créez votre compte{" "}
                    <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                      ConnectRH
                    </span>
                  </>
                )
            }
          </motion.p>
        </motion.div>

        {/* Message de statut avec animation */}
        <AnimatePresence>
          {params.message && (
            <motion.div 
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="mt-6"
            >
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-6 shadow-lg relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <div className="flex items-center space-x-3 relative">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <ShieldCheckIcon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm text-green-700 font-medium">
                    {params.message}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
      >
        <motion.div
          variants={scaleIn}
          whileHover={{ y: -5 }}
          className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-xl py-10 px-8 shadow-2xl rounded-3xl border-2 border-white/30 relative overflow-hidden group"
        >
          {/* Effet de brillance sur le formulaire */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-100/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          
          <form action={handleSubmit} className="space-y-6 relative">
            {/* Champ Email avec style moderne */}
            <motion.div variants={fadeInUp} className="space-y-3">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800">
                Adresse email
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                  <EnvelopeIcon className="h-5 w-5 text-violet-500 group-focus-within:text-violet-600 transition-colors duration-200" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-12 pr-4 py-4 border-2 border-violet-200 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-lg focus:bg-white hover:border-violet-300"
                  placeholder="votre@email.com"
                />
              </div>
            </motion.div>

            {/* Champ Prénom avec animation conditionnelle */}
            <AnimatePresence>
              {!isLogin && !showForgotPassword && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  <label htmlFor="first_name" className="block text-sm font-bold text-gray-800">
                    Prénom
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                      <UserIcon className="h-5 w-5 text-violet-500 group-focus-within:text-violet-600 transition-colors duration-200" />
                    </div>
                    <input
                      id="first_name"
                      name="first_name"
                      type="text"
                      autoComplete="given-name"
                      required
                      className="block w-full pl-12 pr-4 py-4 border-2 border-violet-200 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-lg focus:bg-white hover:border-violet-300"
                      placeholder="Votre prénom"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Champ Mot de passe avec animation conditionnelle */}
            <AnimatePresence>
              {!showForgotPassword && (
                <motion.div 
                  initial={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  <label htmlFor="password" className="block text-sm font-bold text-gray-800">
                    Mot de passe
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                      <LockClosedIcon className="h-5 w-5 text-violet-500 group-focus-within:text-violet-600 transition-colors duration-200" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete={isLogin ? "current-password" : "new-password"}
                      required
                      className="block w-full pl-12 pr-12 py-4 border-2 border-violet-200 rounded-2xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-lg focus:bg-white hover:border-violet-300"
                      placeholder="••••••••"
                    />
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center z-10"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-violet-500 hover:text-violet-700 transition-colors duration-200" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-violet-500 hover:text-violet-700 transition-colors duration-200" />
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Bouton principal avec gradient et animations */}
            <motion.div 
              variants={fadeInUp}
              className="pt-6"
            >
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative w-full flex justify-center items-center py-4 px-6 border border-transparent text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-xl hover:shadow-2xl overflow-hidden"
              >
                {/* Effet de brillance sur le bouton */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
                
                {isLoading ? (
                  <div className="flex items-center space-x-3 relative">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Chargement...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3 relative">
                    <span>
                      {showForgotPassword 
                        ? 'Envoyer le lien' 
                        : isLogin 
                          ? 'Se connecter' 
                          : 'Créer mon compte'
                      }
                    </span>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ArrowRightIcon className="w-5 h-5" />
                    </motion.div>
                  </div>
                )}
              </motion.button>
            </motion.div>

            {/* Navigation entre les modes avec animations */}
            <motion.div 
              variants={fadeInUp}
              className="space-y-6"
            >
              <AnimatePresence mode="wait">
                {showForgotPassword ? (
                  <motion.div 
                    key="forgot-password"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-center"
                  >
                    <motion.button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      whileHover={{ scale: 1.05 }}
                      className="text-sm text-violet-600 hover:text-violet-500 font-bold transition-colors inline-flex items-center space-x-2"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Retour à la connexion</span>
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="login-signup"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="text-center">
                      <motion.button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        whileHover={{ scale: 1.05 }}
                        className="text-sm text-violet-600 hover:text-violet-500 font-medium transition-colors"
                      >
                        Mot de passe oublié ?
                      </motion.button>
                    </div>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-violet-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white/90 text-gray-600 font-medium">ou</span>
                      </div>
                    </div>

                    <div className="text-center">
                      <motion.button
                        type="button"
                        onClick={() => setIsLogin(!isLogin)}
                        whileHover={{ scale: 1.05 }}
                        className="group inline-flex items-center space-x-2 text-sm font-bold text-gray-700 hover:text-violet-600 transition-colors bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-3 rounded-2xl border border-violet-200 hover:border-violet-300 shadow-sm hover:shadow-md"
                      >
                        <UserIcon className="w-4 h-4" />
                        <span>
                          {isLogin ? "Pas de compte ? Créer un compte" : "Déjà un compte ? Se connecter"}
                        </span>
                        <motion.div
                          animate={{ x: [0, 3, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRightIcon className="w-3 h-3" />
                        </motion.div>
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </form>
        </motion.div>

        {/* Footer avec sécurité et style moderne */}
        <motion.div 
          variants={fadeInUp}
          className="mt-10 text-center"
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-violet-50/50 backdrop-blur-sm px-6 py-3 rounded-2xl border border-gray-200/50 shadow-sm"
          >
            <ShieldCheckIcon className="w-4 h-4 text-violet-600" />
            <p className="text-xs text-gray-600 font-medium">
              Connexion sécurisée • Vos données sont protégées
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  )
} 